import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useLanguage } from "../contexts/LanguageContext";
import SEO from "../components/SEO";
import { seoData } from "../utils/seoData";

const Contact = () => {
  const toast = useToast();
  const { language } = useLanguage();
  const seo = seoData.contact[language] || seoData.contact.en;

  const translations = {
    en: {
      heroTitle: "Get in Touch",
      heroDescription:
        "Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
      formTitle: "Send us a Message",
      fields: {
        name: { label: "Name", placeholder: "Your name" },
        email: { label: "Email", placeholder: "your@email.com" },
        company: { label: "Company", placeholder: "Your company name" },
        message: {
          label: "Message",
          placeholder: "Tell us about your project...",
        },
      },
      submitButton: "Send Message",
      loadingText: "Sending...",
      toastTitle: "Message sent!",
      toastDescription: "We'll get back to you as soon as possible.",
    },
    ko: {
      heroTitle: "문의하기",
      heroDescription:
        "궁금한 점이 있으신가요? 언제든지 메시지를 남겨 주세요. 빠르게 답변드리겠습니다.",
      formTitle: "메시지 보내기",
      fields: {
        name: { label: "이름", placeholder: "이름을 입력하세요" },
        email: { label: "이메일", placeholder: "you@example.com" },
        company: {
          label: "회사명",
          placeholder: "회사 또는 기관명을 입력하세요",
        },
        message: {
          label: "메시지",
          placeholder: "프로젝트에 대해 알려주세요...",
        },
      },
      submitButton: "메시지 전송",
      loadingText: "전송 중...",
      toastTitle: "메시지가 전송되었습니다!",
      toastDescription: "가능한 한 빠르게 연락드리겠습니다.",
    },
  };

  const t = translations[language] || translations.en;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formTimestamp] = useState(Date.now()); // Track when form was loaded
  const [honeypot, setHoneypot] = useState(""); // Honeypot field for bot detection

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // AWS Lambda Function URL
      const LAMBDA_URL =
        process.env.REACT_APP_LAMBDA_URL ||
        "https://4lklmdoxowovocx47wpaap4r6i0cmiyy.lambda-url.us-east-1.on.aws/";

      const response = await fetch(LAMBDA_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.company, // company 필드를 phone으로 사용
          message: formData.message,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: t.toastTitle,
          description: t.toastDescription,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setFormData({
          name: "",
          email: "",
          company: "",
          message: "",
        });
      } else {
        throw new Error(data.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast({
        title: language === "ko" ? "오류 발생" : "Error",
        description:
          language === "ko"
            ? "메시지 전송에 실패했습니다. 다시 시도해 주세요."
            : "Failed to send message. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/contact"
        lang={language}
      />
      <Box>
        {/* Hero Section */}
        <Box
          bgGradient="linear(to-br, rgba(255, 106, 95, 0.05), rgba(166, 63, 245, 0.05))"
          py={{ base: 20, md: 28 }}
        >
          <Container maxW="container.xl">
            <VStack spacing={8} textAlign="center">
              <Heading
                fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                fontWeight="bold"
              >
                {t.heroTitle}
              </Heading>
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="gray.600"
                maxW="900px"
                lineHeight="tall"
              >
                {t.heroDescription}
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* Contact Form Section */}
        <Box py={24}>
          <Container maxW="container.md">
            {/* Contact Form */}
            <Box
              bg={useColorModeValue("white", "gray.800")}
              p={{ base: 8, md: 12 }}
              rounded="3xl"
              shadow="2xl"
              border="1px"
              borderColor={useColorModeValue("gray.100", "gray.700")}
            >
              <VStack
                spacing={8}
                align="stretch"
                as="form"
                onSubmit={handleSubmit}
              >
                <Heading
                  fontSize={{ base: "2xl", md: "3xl" }}
                  fontWeight="bold"
                  mb={2}
                >
                  {t.formTitle}
                </Heading>

                {/* Honeypot field - hidden from users, catches bots */}
                <Input
                  name="website"
                  type="text"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ display: "none" }}
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                />

                <FormControl isRequired>
                  <FormLabel fontSize="md" fontWeight="semibold" mb={3}>
                    {t.fields.name.label}
                  </FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t.fields.name.placeholder}
                    size="lg"
                    h="56px"
                    rounded="xl"
                    fontSize="md"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="md" fontWeight="semibold" mb={3}>
                    {t.fields.email.label}
                  </FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.fields.email.placeholder}
                    size="lg"
                    h="56px"
                    rounded="xl"
                    fontSize="md"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="md" fontWeight="semibold" mb={3}>
                    {t.fields.company.label}
                  </FormLabel>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t.fields.company.placeholder}
                    size="lg"
                    h="56px"
                    rounded="xl"
                    fontSize="md"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="md" fontWeight="semibold" mb={3}>
                    {t.fields.message.label}
                  </FormLabel>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t.fields.message.placeholder}
                    rows={6}
                    size="lg"
                    rounded="xl"
                    fontSize="md"
                  />
                </FormControl>

                <Button
                  type="submit"
                  size="lg"
                  h="60px"
                  fontSize="lg"
                  bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
                  color="white"
                  rounded="full"
                  _hover={{
                    bgGradient: "linear(to-r, #FF8FA5, #C56CFF)",
                    transform: "translateY(-4px)",
                    shadow: "2xl",
                  }}
                  transition="all 0.3s"
                  isLoading={isSubmitting}
                  loadingText={t.loadingText}
                >
                  {t.submitButton}
                </Button>
              </VStack>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Contact;
